/* eslint-disable no-underscore-dangle */
/* eslint-disable import/no-cycle */
/* eslint-disable camelcase */
import styled from '@emotion/styled';
import { ui, define, s } from '@owenscorning/pcb.alpha';
import { useState, useEffect } from 'react';

import cmsApi from '../../../../../cms_api';
import { highlightSettingsSection } from '../../../../OC/PageBuilder/utilities/shared';
import createSizes from '../../../../OC/PageBuilder/utilities/sizes';
import { H2 } from '../../../../OC/oc-h';
import { Screens } from '../../../../PageBuilder/editing/Viewport';
import { connectLocation } from '../../../../location/LocationConnectors';
import { digChoicesFromData } from '../../AttributeSet';
import { Renderer as Table } from '../Page/Table';

// Wind resistance choice uids whose row carries the footnote mark.
const HIGHEST_WIND_RESISTANCE_FOOTNOTE_UIDS = ['160mph'];
const HIGHEST_WIND_RESISTANCE_FOOTNOTE = '*/###';

const getTableCell = (text, isBold, footnoteCharacter) => {
  const cell = {
    content: {
      ops: [
        {
          attributes: { bold: isBold },
          insert: text,
        },
        {
          attributes: { script: 'super' },
          insert: footnoteCharacter,
        },
      ],
    },
  };

  return cell;
};

const getTableRow = (attribute, value, footnoteCharacter) => {
  // filter out hidden values for the editor preview's sake,
  // flatten out what remains (if anything) to a string
  const filteredValue = value?.filter?.(v => !v.__hidden) ?? value;
  if (filteredValue?.length === 0) return null;
  const flattenedValue = value?.[0]?.value ?? value;
  if (!flattenedValue) return null;
  return [getTableCell(attribute, true, footnoteCharacter), getTableCell(flattenedValue, false)];
};

const getTableRows = (t, attributeChoiceData) => {
  // values with choice-based labels
  let { algae_resistance, warranty, wind_resistance } = Board.settings.attributes;

  const windResistanceUid = wind_resistance;
  algae_resistance = attributeChoiceData?.algae_resistance?.of?.find(
    c => c.value === algae_resistance?.[0]?.value
  )?.label;
  warranty = attributeChoiceData?.warranty?.of?.find(c => c.value === warranty)?.label;
  wind_resistance = attributeChoiceData?.wind_resistance?.of?.find(c => c.value === wind_resistance)?.label;
  const windResistanceFootnote = HIGHEST_WIND_RESISTANCE_FOOTNOTE_UIDS.includes(windResistanceUid)
    ? HIGHEST_WIND_RESISTANCE_FOOTNOTE
    : undefined;

  // everything else
  const { nominal_size, exposure, shingles_per_sq, bundles_per_sq, coverage_per_sq } = Board.settings.attributes;

  return [
    [getTableCell(t('shingles.property_unit'), true), getTableCell(t('shingles.value'), true)],
    getTableRow(t('shingles.warranty'), warranty),
    getTableRow(t('shingles.wind_resistance'), wind_resistance, windResistanceFootnote),
    getTableRow(t('shingles.algae_resistance'), algae_resistance, '§'),
    getTableRow(t('shingles.nominal_size'), nominal_size),
    getTableRow(t('shingles.exposure'), exposure),
    getTableRow(t('shingles.shingles_per_sq'), shingles_per_sq),
    getTableRow(t('shingles.bundles_per_sq'), bundles_per_sq),
    getTableRow(t('shingles.coverage_per_sq'), coverage_per_sq),
  ].filter(row => row !== null);
};

const getTableData = (t, attributeChoiceData) => [
  {
    rows: getTableRows(t, attributeChoiceData),
    type: 'Body',
  },
];

const Sizes = createSizes();
const Width = Sizes.m;
const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto 32px auto;
  @media (min-width: ${Screens.Tablet}px) {
    width: ${({ width }) => `${width.Tablet}px`};
  }
  @media (min-width: ${Screens.Desktop}px) {
    width: ${({ width }) => `${width.Desktop}px`};
  }
`;

const HeaderSmallerText = styled.span`
  font-size: 16px;
`;

const Renderer = connectLocation(({ t }) => {
  const [attributeChoiceData, setAttributeChoiceData] = useState({});
  useEffect(async () => {
    const query = {
      filter: {
        type: 'Cms::Content::AttributeSet',
        name: 'Shingle PDP Attributes',
        route: '/roofing',
      },
      fields: {
        '*': 'contents,metadata',
      },
    };
    const data = await cmsApi.get_single_for_language(query, Board.build.language);

    // algae_resistance's list seems to get ignored by digChoicesFromData, lift up the choices
    const algaeResistanceData = data?.contents?.items?.find(i => i.field === 'algae_resistance');
    if (algaeResistanceData) {
      data.contents.items[data?.contents?.items?.indexOf(algaeResistanceData)] = {
        field: algaeResistanceData.field,
        label: algaeResistanceData.label,
        ...algaeResistanceData?.list?.of,
      };
    }

    const choices = await digChoicesFromData('', data);
    setAttributeChoiceData(choices);
  }, []);

  return (
    <TableContainer width={Width}>
      <H2 font="body">
        {t('shingles.technical_characteristics')} <HeaderSmallerText>{t('shingles.nominal_values')}</HeaderSmallerText>
      </H2>
      <Table alignment="center" containerSize="m" table={getTableData(t, attributeChoiceData)} />
    </TableContainer>
  );
});

const TechnicalCharacteristicsTable = define`Technical Characteristics Table`('0.0.1')({
  thumbnail: ui`Modules/Page/Thumbnails/Table`,
  view: <Renderer />,
  edit: {
    [s._]: ui`Tip`.of('Technical Characteristics come from Attributes in Settings')({ noTopMargin: true }),
    [s._]: ui`RawLink`.of('Go to settings')({
      onClick: () => {
        highlightSettingsSection('sidebar-pane', 'attributes');
      },
      noTopMargin: true,
      icon: 'external-link',
    }),
  },
});

export default TechnicalCharacteristicsTable;
